import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { BookOpen, Activity, AlertTriangle, ClipboardCheck } from 'lucide-react';

const fmt = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const fmtDt = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const FINDING_TYPE_STYLE = {
  'Changed':        'bg-gray-100 text-gray-700 border border-gray-200',
  'Conflict':       'bg-black text-white border border-black',
  'Stale':          'bg-gray-100 text-gray-600 border border-gray-200',
  'Missing source': 'bg-gray-50 text-gray-500 border border-gray-200',
};

export default function Overview() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/overview')
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-body text-gray-500 py-8">Loading overview…</p>;
  if (error)   return <p className="font-body text-gray-700 py-8">Unable to load overview: {error}</p>;

  const { stats, review_items, recent_changes, recent_decisions } = data;

  const STAT_CARDS = [
    { label: 'Sources',   value: stats.sources,   icon: BookOpen,       desc: 'Registered source artifacts' },
    { label: 'Changes',   value: stats.changes,   icon: Activity,       desc: 'Pending sentinel events' },
    { label: 'Conflicts', value: stats.conflicts, icon: AlertTriangle,  desc: 'Open conflict findings' },
    { label: 'Reviews',   value: stats.reviews,   icon: ClipboardCheck, desc: 'Findings needing a decision' },
  ];

  return (
    <div data-testid="overview-page">
      {/* Page heading */}
      <h1 className="page-heading mb-2">
        Keep your organization's knowledge current, clear, and governed.
      </h1>
      <p className="font-body mb-8 max-w-2xl" style={{ fontSize: '15px', color: '#667085', lineHeight: 1.5 }}>
        See what is approved, what changed, where sources disagree, and who needs to decide.
      </p>

      {/* Summary cards — single col on mobile, 2 col on sm, 4 col on xl */}
      <div
        className="grid gap-4 mb-10"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))' }}
        data-testid="stats-grid"
      >
        {STAT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className="rounded p-5 bg-white"
              style={{ border: '1px solid var(--ky-border)', minWidth: 0 }}
              data-testid={`stat-card-${card.label.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  className="font-heading font-semibold uppercase tracking-widest"
                  style={{ fontSize: '11px', color: '#98a2b3', letterSpacing: '0.08em' }}
                >
                  {card.label}
                </span>
                <Icon size={14} strokeWidth={2} style={{ color: '#d9dee6' }} />
              </div>
              <div
                className="font-heading font-bold text-black"
                style={{ fontSize: '32px', lineHeight: 1 }}
                data-testid={`stat-value-${card.label.toLowerCase()}`}
              >
                {card.value}
              </div>
              <p className="font-body mt-1.5" style={{ fontSize: '13px', color: '#667085' }}>
                {card.desc}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_0.95fr] gap-6">
        {/* Items needing review */}
        <div data-testid="review-items-section">
          <h2 className="section-heading mb-4">Items needing review</h2>
          {review_items.length === 0 ? (
            <p className="font-body py-4" style={{ fontSize: '14px', color: '#667085' }}>No items currently need review.</p>
          ) : (
            <div className="space-y-2">
              {review_items.map((item) => (
                <Link
                  key={item.id}
                  to={`/app/brain/${item.id}`}
                  className="flex items-center gap-3 rounded transition-colors hover:bg-gray-50"
                  style={{ padding: '16px', minHeight: '72px', border: '1px solid var(--ky-border)', display: 'flex' }}
                  data-testid={`review-item-${item.id}`}
                >
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded shrink-0 font-heading font-medium ${FINDING_TYPE_STYLE[item.finding_type] || 'bg-gray-100 text-gray-600'}`}
                    style={{ fontSize: '12px' }}
                  >
                    {item.finding_type}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-medium text-black leading-snug" style={{ fontSize: '14px' }}>
                      {item.title}
                    </p>
                    <p className="font-body mt-0.5 truncate" style={{ fontSize: '13px', color: '#667085' }}>
                      Assigned to {item.assigned_authority}
                    </p>
                  </div>
                  {item.abstain && (
                    <span className="ml-auto shrink-0 font-heading font-semibold" style={{ fontSize: '12px', color: '#000' }}>
                      ABSTAIN
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Recent changes */}
          <div data-testid="recent-changes-section">
            <h2 className="section-heading mb-4">Recent changes</h2>
            {recent_changes.length === 0 ? (
              <p className="font-body" style={{ fontSize: '14px', color: '#667085' }}>No recent changes.</p>
            ) : (
              <div className="space-y-2">
                {recent_changes.map((ev) => (
                  <Link
                    key={ev.id}
                    to="/app/sentinel"
                    className="block rounded transition-colors hover:bg-gray-50"
                    style={{ padding: '14px 16px', border: '1px solid var(--ky-border)' }}
                    data-testid={`recent-change-${ev.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{
                          backgroundColor: ev.impact_level === 'High' ? '#000' :
                            ev.impact_level === 'Medium' ? '#667085' : '#d9dee6'
                        }}
                      />
                      <span className="font-heading font-medium" style={{ fontSize: '12px', color: '#667085' }}>
                        {ev.impact_level}
                      </span>
                    </div>
                    <p className="font-body text-black font-medium leading-snug" style={{ fontSize: '13px' }}>
                      {ev.affected_source}
                    </p>
                    <p className="font-body mt-0.5" style={{ fontSize: '13px', color: '#667085' }}>{ev.change_type}</p>
                    <p className="font-body mt-0.5" style={{ fontSize: '12px', color: '#98a2b3' }}>{fmt(ev.detected_date)}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Recent decisions */}
          <div data-testid="recent-decisions-section">
            <h2 className="section-heading mb-4">Recent decisions</h2>
            {recent_decisions.length === 0 ? (
              <p className="font-body" style={{ fontSize: '14px', color: '#667085' }}>No recent decisions.</p>
            ) : (
              <div className="space-y-2">
                {recent_decisions.map((ev) => (
                  <Link
                    key={ev.id}
                    to="/app/audit"
                    className="block rounded transition-colors hover:bg-gray-50"
                    style={{ padding: '14px 16px', border: '1px solid var(--ky-border)' }}
                    data-testid={`recent-decision-${ev.id}`}
                  >
                    <p className="font-body text-black font-medium leading-snug" style={{ fontSize: '13px' }}>
                      {ev.action}
                    </p>
                    <p className="font-body mt-0.5 truncate" style={{ fontSize: '13px', color: '#667085' }}>
                      {ev.actor} · {ev.role}
                    </p>
                    <p className="font-body mt-0.5" style={{ fontSize: '12px', color: '#98a2b3' }}>{fmtDt(ev.timestamp)}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
